import { Injectable, NgZone } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class PerformanceTimingService {
  private readonly navigationMarks = new Map<number, string>();
  private started = false;

  constructor(private router: Router, private zone: NgZone) {}

  start(): void {
    if (this.started || !this.canMeasure()) {
      return;
    }

    this.started = true;
    this.mark('propdesk:app:interactive');
    this.measure('propdesk:app:bootstrap', 'propdesk:app:start', 'propdesk:app:interactive');

    this.zone.runOutsideAngular(() => {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          const startMark = `propdesk:navigation:${event.id}:start`;
          this.navigationMarks.set(event.id, startMark);
          this.mark(startMark);
          return;
        }

        if (event instanceof NavigationEnd) {
          this.finishNavigation(event.id, event.urlAfterRedirects, 'complete');
          return;
        }

        if (event instanceof NavigationCancel || event instanceof NavigationError) {
          this.finishNavigation(event.id, event.url, 'interrupted');
        }
      });
    });
  }

  markFeature(name: string): void {
    if (!this.canMeasure()) {
      return;
    }

    this.mark(`propdesk:feature:${this.normalizeName(name)}`);
  }

  private finishNavigation(id: number, url: string, status: 'complete' | 'interrupted'): void {
    const startMark = this.navigationMarks.get(id);
    if (!startMark) {
      return;
    }

    const safeUrl = this.normalizeName(url || 'unknown');
    const endMark = `propdesk:navigation:${id}:end`;
    this.mark(endMark);
    this.measure(`propdesk:navigation:${safeUrl}:${status}`, startMark, endMark);
    this.navigationMarks.delete(id);
  }

  private mark(name: string): void {
    performance.mark(name);
  }

  private measure(name: string, startMark: string, endMark: string): void {
    try {
      performance.measure(name, startMark, endMark);
    } catch {
      // Missing marks should not affect the app experience.
    }
  }

  private canMeasure(): boolean {
    return typeof performance !== 'undefined'
      && typeof performance.mark === 'function'
      && typeof performance.measure === 'function';
  }

  private normalizeName(value: string): string {
    return value
      .toLowerCase()
      .replace(/^https?:\/\/[^/]+/i, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      || 'root';
  }
}
